import React, { Component } from 'react';
import { AppState } from 'state';
import { Redirect } from 'react-router-dom';

import { Unauthorized } from 'ui/components';

/**
 * Check if user has the rights required.
 *
 * @export
 * @param {any} neededRights - What rights the user needs
 * @returns {Bool} True if user has the necessary rights
 */
export function isAuthorized(neededRights) {
	return neededRights.every(right => AppState.userRights.includes(right));
}

/**
 * Authorization decorator user in the authorize function
 *
 * @export
 * @param {Object} config { rights: ownRights, redirectTo }
 * @returns {Component|null} The Component or null
 */
export function authorizeDecorator({
	rights: ownRights,
	redirectTo,
	Instead = Unauthorized,
}) {
	return function authorizeDecoratorFn(WrappedComponent) {
		return class extends Component {
			isAuthorized() {
				return isAuthorized(ownRights);
			}

			render() {
				let component = null;

				if (this.isAuthorized()) {
					component = <WrappedComponent {...this.props} />;
				} else if (redirectTo) {
					component = <Redirect to={redirectTo} />;
				} else {
					component = <Instead />;
				}

				return component;
			}
		};
	};
}

/**
 * Authorize a component inline
 *
 * @export
 * @param {Array} rights - The rights that are needed to display the component
 * @param {Component} component - The component to be displayed if it has the rights
 * @param {Component|null} instead -The component to be displayed if it doesn't have the rights
 * @returns {Component|null} - The component or null
 */
export function authorizeComponent(rights, component, instead = null) {
	return isAuthorized(rights) ? component : instead;
}

/**
 * Authorization decorator to display components based on their required
 * rights and the current user's rights
 *
 * @example
 *  @authorize({ rights: ['GetOrderHistory'] })
 *  @inject('SearchState')
 *  @observer
 *  export class MyAcomponent extends Component { ... }
 *
 *  @returns {Component|null} - The component or null
 */
export function authorize(...args) {
	if (Array.isArray(args[0])) {
		return authorizeComponent(...args);
	}

	return authorizeDecorator(...args);
}



// WEBPACK FOOTER //
// ./src/utils/auth.js